import serviceAPI from 'api/service/serviceAPI';
import { Dispatch, AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { StateModel } from '../reducers';
import { fetchProfile, signOut } from './auth';
import { isAccessTokenAlive } from 'helpers/commonHelpers';
import { ClientGroupStatuses } from 'enums/onboarding/crm';
import { getGeolocationStatus } from 'helpers/sessionHelpers';
import { USER_TAG_IS_LOGGED_IN_FIRST_TIME } from 'constants/userTags';
import { ACTIVE_ANNOUNCEMENT_MESSAGE_VERSION } from 'constants/announcementModal';
import { AppTrace } from 'services/azure-app-insights/appTrace';
import { clearLocalStorage, getAccessToken } from 'helpers/localStorageHelpers';
import {
  clearUserSession,
  consumeShowAnnouncementAfterLogin,
  getUserSession,
  hasShowAnnouncementAfterLogin,
} from 'helpers/sessionStorageHelpers';

// Actions
export enum ActionType {
  SET_BADGES = 'app/SET_BADGES',
  SET_INITIALIZED_STATUS = 'app/SET_INITIALIZED_STATUS',
  SET_ACTIVITY_TIMESTAMP = 'app/SET_ACTIVITY_TIMESTAMP',
  SET_APP_INSIGHTS_CONNECTION_STRINGS = 'app/SET_APP_INSIGHTS_CONNECTION_STRINGS',
  SET_WELCOME_MODAL_VISIBILITY_STATUS = 'app/SET_WELCOME_MODAL_VISIBILITY_STATUS',
  SET_ANNOUNCEMENT_VISIBILITY_STATUS = 'app/SET_ANNOUNCEMENT_VISIBILITY_STATUS',
  SET_ANNOUNCEMENT_RECHECK_PENDING_STATUS = 'app/SET_ANNOUNCEMENT_RECHECK_PENDING_STATUS',
  CLEAN_UP = 'app/CLEAN_UP',
}

export type AnnouncementInitializationResult = 'shown' | 'skipped' | 'deferred';

export type AnnouncementInitializationDecision = {
  status: AnnouncementInitializationResult;
  shouldConsumeFlag: boolean;
  shouldSetRecheckPending: boolean;
  shouldShowAnnouncement: boolean;
};

type AnnouncementDecisionInput = {
  isAuthorizedPostLoginContext: boolean;
  hasFreshLoginFlag: boolean;
  hasActiveApplication: boolean;
  isApprovedClientGroupSelected: boolean;
  activeAnnouncementMessageVersion: string | null;
};

export const getAnnouncementInitializationDecision = ({
  isAuthorizedPostLoginContext,
  hasFreshLoginFlag,
  hasActiveApplication,
  isApprovedClientGroupSelected,
  activeAnnouncementMessageVersion,
}: AnnouncementDecisionInput): AnnouncementInitializationDecision => {
  if (activeAnnouncementMessageVersion === null) {
    return {
      status: 'skipped',
      shouldConsumeFlag: hasFreshLoginFlag,
      shouldSetRecheckPending: false,
      shouldShowAnnouncement: false,
    };
  }

  if (!hasActiveApplication) {
    const shouldRecheckAfterContextSelection =
      isAuthorizedPostLoginContext && hasFreshLoginFlag;
    return {
      status: shouldRecheckAfterContextSelection ? 'deferred' : 'skipped',
      shouldConsumeFlag: false,
      shouldSetRecheckPending: shouldRecheckAfterContextSelection,
      shouldShowAnnouncement: false,
    };
  }

  if (!isAuthorizedPostLoginContext) {
    return {
      status: 'skipped',
      shouldConsumeFlag: true,
      shouldSetRecheckPending: false,
      shouldShowAnnouncement: false,
    };
  }

  if (!hasFreshLoginFlag) {
    return {
      status: 'skipped',
      shouldConsumeFlag: false,
      shouldSetRecheckPending: false,
      shouldShowAnnouncement: false,
    };
  }

  if (!isApprovedClientGroupSelected) {
    return {
      status: 'skipped',
      shouldConsumeFlag: true,
      shouldSetRecheckPending: false,
      shouldShowAnnouncement: false,
    };
  }

  return {
    status: 'shown',
    shouldConsumeFlag: true,
    shouldSetRecheckPending: false,
    shouldShowAnnouncement: true,
  };
};

// Action creators
export const setInitializedStatus = (newStatus: boolean) => ({
  type: ActionType.SET_INITIALIZED_STATUS,
  payload: { status: newStatus },
});

export const setLastActivityTimestamp = (lastActivityTimestamp: number) => ({
  type: ActionType.SET_ACTIVITY_TIMESTAMP,
  payload: { lastActivityTimestamp },
});

export const cleanUpAppReducer = () => ({
  type: ActionType.CLEAN_UP,
  payload: null,
});

export const setWelcomeModalVisibilityStatus = (status: boolean) => ({
  type: ActionType.SET_WELCOME_MODAL_VISIBILITY_STATUS,
  payload: { status },
});

export const setAnnouncementVisibilityStatus = (
  status: boolean,
  messageVersion: string | null = null,
) => ({
  type: ActionType.SET_ANNOUNCEMENT_VISIBILITY_STATUS,
  payload: {
    status,
    messageVersion: status ? messageVersion : null,
  },
});

export const setAnnouncementRecheckPendingStatus = (status: boolean) => ({
  type: ActionType.SET_ANNOUNCEMENT_RECHECK_PENDING_STATUS,
  payload: { status },
});

export const initializeApp = () => async (dispatch: any) => {
  dispatch(setInitializedStatus(false));

  const geolocationStatus = await getGeolocationStatus();
  if (geolocationStatus === 'denied') {
    clearUserSession();
  }

  if (geolocationStatus === 'other-no-access') {
    const currentSession = getUserSession();
    if (currentSession && currentSession.geolocation) {
      clearUserSession();
    }
  }

  const accessToken = getAccessToken();
  if (!accessToken || (accessToken && !isAccessTokenAlive(accessToken))) {
    dispatch(setInitializedStatus(true));
    clearLocalStorage();
  } else {
    dispatch(initializeAppForAuthorizedUser());
  }
};

// TODO:
// 1) Update the logic for checking if the client group is approved (need to check from the user permissions)
export const initializeWelcomeModalStatus =
  () => (dispatch: Dispatch, getState: () => StateModel) => {
    const { auth, applications } = getState();

    const isUserLoggedInFirstTime = auth.profileData?.tags.includes(
      USER_TAG_IS_LOGGED_IN_FIRST_TIME,
    );
    const isActiveApplicationApproved =
      applications.activeApplication?.clientGroup?.status ===
      ClientGroupStatuses.Approved;

    if (isUserLoggedInFirstTime && isActiveApplicationApproved) {
      dispatch(setWelcomeModalVisibilityStatus(true));
    }
  };

export const initializeAnnouncementStatus =
  () => (dispatch: Dispatch, getState: () => StateModel) => {
    const { auth, applications, app } = getState();
    const activeAnnouncementMessageVersion =
      ACTIVE_ANNOUNCEMENT_MESSAGE_VERSION;
    const isAuthorizedPostLoginContext = auth.isAuthorized;
    const hasFreshLoginFlag = hasShowAnnouncementAfterLogin();
    const hasActiveApplication = Boolean(applications.activeApplication);
    const isApprovedClientGroupSelected =
      applications.activeApplication?.clientGroup?.status ===
      ClientGroupStatuses.Approved;
    const shouldPreserveCurrentlyVisibleAnnouncement =
      app.isAnnouncementVisible &&
      app.announcementMessageVersion === activeAnnouncementMessageVersion;

    const decision = getAnnouncementInitializationDecision({
      isAuthorizedPostLoginContext,
      hasFreshLoginFlag,
      hasActiveApplication,
      isApprovedClientGroupSelected,
      activeAnnouncementMessageVersion,
    });

    dispatch(
      setAnnouncementRecheckPendingStatus(decision.shouldSetRecheckPending),
    );

    if (decision.shouldConsumeFlag) {
      consumeShowAnnouncementAfterLogin();
    }

    if (decision.shouldShowAnnouncement && activeAnnouncementMessageVersion) {
      AppTrace.announcementShown(activeAnnouncementMessageVersion);
      dispatch(
        setAnnouncementVisibilityStatus(true, activeAnnouncementMessageVersion),
      );
      return decision.status;
    }

    if (!shouldPreserveCurrentlyVisibleAnnouncement) {
      dispatch(setAnnouncementVisibilityStatus(false, null));
    }
    return decision.status;
  };

export const recheckAnnouncementAfterContextSelection =
  () =>
  (
    dispatch: ThunkDispatch<StateModel, unknown, AnyAction>,
    getState: () => StateModel,
  ) => {
    if (!getState().app.isAnnouncementRecheckPending) {
      return;
    }

    const announcementInitializationResult = dispatch(
      initializeAnnouncementStatus(),
    );

    if (announcementInitializationResult !== 'shown') {
      dispatch(initializeWelcomeModalStatus());
    }
  };

export const dismissAnnouncementAndInitializeWelcome =
  () => (dispatch: ThunkDispatch<StateModel, unknown, AnyAction>) => {
    dispatch(setAnnouncementVisibilityStatus(false, null));
    dispatch(initializeWelcomeModalStatus());
  };

export const initializeAppForAuthorizedUser = () => async (dispatch: any) => {
  try {
    await dispatch(initializeAppInsights());
    await dispatch(fetchProfile());
    dispatch(setInitializedStatus(true));
    const announcementInitializationResult = dispatch(
      initializeAnnouncementStatus(),
    );

    if (announcementInitializationResult !== 'shown') {
      dispatch(initializeWelcomeModalStatus());
    }
  } catch {
    dispatch(signOut());
  }
};

const initializeAppInsights = () => async (dispatch: Dispatch) => {
  const response = await serviceAPI.fetchFrontendConfig();

  dispatch({
    type: ActionType.SET_APP_INSIGHTS_CONNECTION_STRINGS,
    payload: { connectionString: response.insights.connectionString },
    key: 'connectionString',
  });
};

export const getBadges = () => async (dispatch: Dispatch) => {
  const response = await serviceAPI.fetchBadges();

  dispatch({
    type: ActionType.SET_BADGES,
    payload: {
      badges: {
        pendingTransactionsCount: response.pendingApprovalTransactions,
        pendingClientRequestsCount: response.clientRequests,
        pendingAccountManagementWorkflowsCount:
          response.pendingAccountManagementWorkflows,
      },
    },
    key: 'badges',
  });
};
